import React, { useEffect, useState } from "react"
import { Loader2, Save } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Switch } from "src/components/ui/switch"
import { Label } from "src/components/ui/label"
import { Checkbox } from "src/components/ui/checkbox"
import { Input } from "src/components/ui/input"
import { useToast } from "src/components/ui/toast"
import { fetchReminderSettings } from "../api/collectionsApi"
import { useUpdateReminderSettings } from "../hooks/useCollectionMutations"
import { OFFSET_LABELS, REMINDER_CHANNELS } from "../constants"

export default function ReminderSettingsForm() {
  const { toast } = useToast()
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const { mutate: save, isPending: saving } = useUpdateReminderSettings()

  useEffect(() => {
    fetchReminderSettings()
      .then(setSettings)
      .catch(() => toast({ variant: "destructive", title: "Error", description: "Failed to load settings." }))
      .finally(() => setLoading(false))
  }, [])

  function updateField(key, value) {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  function toggleArrayItem(key, item) {
    setSettings((prev) => {
      const arr = prev[key] || []
      const next = arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item]
      return { ...prev, [key]: next }
    })
  }

  async function handleSave() {
    try {
      const updated = await save(settings)
      setSettings(updated)
      toast({ title: "Settings saved", description: "Reminder settings updated successfully." })
    } catch (errors) {
      toast({ variant: "destructive", title: "Error", description: errors?.[0] || "Failed to save settings." })
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading settings…
      </div>
    )
  }

  if (!settings) return null

  return (
    <div className="mx-auto max-w-[700px]">
      <div className="rounded-lg border border-slate-200 bg-white">
        {/* Enable toggle */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <p className="text-[14px] font-medium text-slate-900">Automated Reminders</p>
            <p className="text-[12px] text-slate-500">
              Automatically send payment reminders to customers based on due dates.
            </p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(checked) => updateField("enabled", checked)}
          />
        </div>

        <div className={settings.enabled ? "" : "pointer-events-none opacity-50"}>
          {/* Channels */}
          <div className="border-b border-slate-100 px-6 py-4">
            <Label className="text-[13px] font-medium text-slate-700">Channels</Label>
            <div className="mt-2 flex flex-wrap gap-4">
              {REMINDER_CHANNELS.map((ch) => (
                <label key={ch.value} className="flex items-center gap-2 text-[13px] text-slate-700">
                  <Checkbox
                    checked={(settings.channels || []).includes(ch.value)}
                    onCheckedChange={() => toggleArrayItem("channels", ch.value)}
                  />
                  {ch.label}
                </label>
              ))}
            </div>
          </div>

          {/* Offsets */}
          <div className="border-b border-slate-100 px-6 py-4">
            <Label className="text-[13px] font-medium text-slate-700">Reminder Schedule</Label>
            <p className="mb-2 text-[12px] text-slate-400">
              Choose when reminders are sent relative to the due date.
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(OFFSET_LABELS).map(([key, label]) => {
                const isActive = (settings.offsets || []).includes(key)
                return (
                  <Button
                    key={key}
                    variant="outline"
                    onClick={() => toggleArrayItem("offsets", key)}
                    className={`h-auto rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ${
                      isActive
                        ? "border-slate-900 bg-slate-900 text-white hover:bg-slate-800 hover:text-white"
                        : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                    }`}
                  >
                    {label}
                  </Button>
                )
              })}
            </div>
          </div>

          {/* Quiet Hours */}
          <div className="px-6 py-4">
            <Label className="text-[13px] font-medium text-slate-700">Quiet Hours</Label>
            <p className="mb-2 text-[12px] text-slate-400">
              Reminders won't be sent during these hours.
            </p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] text-slate-500">From</span>
                <Input
                  type="time"
                  value={settings.quiet_hours?.start || "21:00"}
                  onChange={(e) =>
                    updateField("quiet_hours", {
                      ...settings.quiet_hours,
                      start: e.target.value,
                    })
                  }
                  className="w-28 text-[13px]"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] text-slate-500">To</span>
                <Input
                  type="time"
                  value={settings.quiet_hours?.end || "08:00"}
                  onChange={(e) =>
                    updateField("quiet_hours", {
                      ...settings.quiet_hours,
                      end: e.target.value,
                    })
                  }
                  className="w-28 text-[13px]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end border-t border-slate-100 px-6 py-4">
          <Button onClick={handleSave} disabled={saving} className="gap-1.5">
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </div>
      </div>
    </div>
  )
}
